import React from "react";
import { formatDateWithoutHour } from "../utils/date.js";

/**
 * Componente para exibir detalhes de pagamento de forma visual
 * Exibe informações específicas baseadas na forma de pagamento
 */
const DetalhesPagamentoView = ({ pedido }) => {
    const formaPagamento = pedido.formaPagamento;
    const isPagamentoParcelado = pedido.parcelamentoParcelas > 0;

    const sectionStyle = {
        backgroundColor: "#F9FAFB",
        padding: "20px",
        borderRadius: "12px",
        marginBottom: "20px",
        border: "1px solid #E5E7EB"
    };

    const titleStyle = {
        fontSize: "16px",
        fontWeight: "600",
        marginBottom: "16px",
        color: "#1F2937",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    };

    const itemStyle = {
        display: "flex",
        justifyContent: "space-between",
        padding: "12px 0",
        borderBottom: "1px solid #E5E7EB"
    };

    const labelStyle = {
        fontSize: "14px",
        color: "#6B7280",
        fontWeight: "500"
    };

    const valueStyle = {
        fontSize: "14px",
        color: "#111827",
        fontWeight: "600"
    };

    // Renderizar detalhes do PIX
    const renderPixDetails = () => {
        if (!pedido.pixChave) return null;

        const tipoChaveMap = {
            'CPF': '📄 CPF',
            'CNPJ': '🏢 CNPJ',
            'EMAIL': '📧 E-mail',
            'TELEFONE': '📱 Telefone',
            'ALEATORIA': '🔑 Chave Aleatória'
        };

        return (
            <div style={sectionStyle}>
                <h3 style={titleStyle}>
                    <span>📱</span>
                    Detalhes do PIX
                </h3>
                
                <div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Tipo de Chave:</span>
                        <span style={valueStyle}>{tipoChaveMap[pedido.pixTipoChave] || pedido.pixTipoChave}</span>
                    </div>
                    <div style={{ ...itemStyle, borderBottom: pedido.pixVencimento ? "1px solid #E5E7EB" : "none" }}>
                        <span style={labelStyle}>Chave PIX:</span>
                        <span style={{ ...valueStyle, fontFamily: "monospace" }}>{pedido.pixChave}</span>
                    </div>
                    {pedido.pixVencimento && (
                        <div style={{ ...itemStyle, borderBottom: "none" }}>
                            <span style={labelStyle}>Vencimento:</span>
                            <span style={valueStyle}>{formatDateWithoutHour(pedido.pixVencimento)}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Renderizar detalhes do parcelamento
    const renderParcelamentoDetails = () => {
        if (!pedido.parcelamentoParcelas) return null;

        const intervaloMap = {
            'SEMANAL': 'Semanal',
            'QUINZENAL': 'Quinzenal',
            'MENSAL': 'Mensal',
            'BIMESTRAL': 'Bimestral',
            'TRIMESTRAL': 'Trimestral',
            'SEMESTRAL': 'Semestral'
        };

        return (
            <div style={sectionStyle}>
                <h3 style={titleStyle}>
                    <span>💳</span>
                    Detalhes do Parcelamento
                </h3>
                
                <div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Número de Parcelas:</span>
                        <span style={valueStyle}>{pedido.parcelamentoParcelas}x</span>
                    </div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Intervalo:</span>
                        <span style={valueStyle}>{intervaloMap[pedido.parcelamentoIntervalo] || pedido.parcelamentoIntervalo}</span>
                    </div>
                    {pedido.parcelamentoDataPrimeira && (
                        <div style={itemStyle}>
                            <span style={labelStyle}>Data 1ª Parcela:</span>
                            <span style={valueStyle}>{formatDateWithoutHour(pedido.parcelamentoDataPrimeira)}</span>
                        </div>
                    )}
                    {pedido.valorTotal && pedido.parcelamentoParcelas && (
                        <div style={itemStyle}>
                            <span style={labelStyle}>Valor por Parcela:</span>
                            <span style={{ ...valueStyle, color: "#059669" }}>
                                R$ {(pedido.valorTotal / pedido.parcelamentoParcelas).toFixed(2).replace('.', ',')}
                            </span>
                        </div>
                    )}
                    {pedido.parcelamentoObservacao && (
                        <div style={{ ...itemStyle, borderBottom: "none", flexDirection: "column", gap: "8px" }}>
                            <span style={labelStyle}>Observações:</span>
                            <span style={{ ...valueStyle, fontSize: "13px", fontWeight: "400" }}>
                                {pedido.parcelamentoObservacao}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Renderizar dados bancários
    const renderDadosBancarios = () => {
        if (!pedido.bancoNome) return null;

        return (
            <div style={sectionStyle}>
                <h3 style={titleStyle}>
                    <span>🏦</span>
                    Dados Bancários
                </h3>
                
                <div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Banco:</span>
                        <span style={valueStyle}>{pedido.bancoNome} ({pedido.bancoCodigo})</span>
                    </div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Agência:</span>
                        <span style={{ ...valueStyle, fontFamily: "monospace" }}>{pedido.bancoAgencia}</span>
                    </div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Conta:</span>
                        <span style={{ ...valueStyle, fontFamily: "monospace" }}>
                            {pedido.bancoConta} ({pedido.bancoTipoConta === 'CORRENTE' ? 'Corrente' : 'Poupança'})
                        </span>
                    </div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Favorecido:</span>
                        <span style={valueStyle}>{pedido.bancoFavorecido}</span>
                    </div>
                    {pedido.bancoCnpjCpfFavorecido && (
                        <div style={itemStyle}>
                            <span style={labelStyle}>CPF/CNPJ:</span>
                            <span style={{ ...valueStyle, fontFamily: "monospace" }}>{pedido.bancoCnpjCpfFavorecido}</span>
                        </div>
                    )}
                    {pedido.transferenciaVencimento && (
                        <div style={{ ...itemStyle, borderBottom: "none" }}>
                            <span style={labelStyle}>Vencimento:</span>
                            <span style={valueStyle}>{formatDateWithoutHour(pedido.transferenciaVencimento)}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Renderizar detalhes do boleto
    // Só exibe se NÃO for pagamento parcelado (boletos múltiplos são exibidos na seção de anexos)
    const renderBoletoDetails = () => {
        if (!pedido.boletoVencimento || isPagamentoParcelado) return null;

        return (
            <div style={sectionStyle}>
                <h3 style={titleStyle}>
                    <span>📄</span>
                    Detalhes do Boleto
                </h3>
                
                <div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Vencimento:</span>
                        <span style={valueStyle}>{formatDateWithoutHour(pedido.boletoVencimento)}</span>
                    </div>
                    {pedido.boletoNumero && (
                        <div style={itemStyle}>
                            <span style={labelStyle}>Número do Boleto:</span>
                            <span style={{ ...valueStyle, fontFamily: "monospace" }}>{pedido.boletoNumero}</span>
                        </div>
                    )}
                    {pedido.boletoCodigoBarras && (
                        <div style={{ ...itemStyle, borderBottom: "none" }}>
                            <span style={labelStyle}>Código de Barras:</span>
                            <span style={{ ...valueStyle, fontFamily: "monospace", fontSize: "12px" }}>
                                {pedido.boletoCodigoBarras}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Renderizar detalhes do cartão
    const renderCartaoDetails = () => {
        if (!pedido.cartaoTipo) return null;

        const tipoCartaoMap = {
            'CREDITO': 'Crédito',
            'DEBITO': 'Débito'
        };

        return (
            <div style={sectionStyle}>
                <h3 style={titleStyle}>
                    <span>💳</span>
                    Detalhes do Cartão
                </h3>
                
                <div>
                    <div style={itemStyle}>
                        <span style={labelStyle}>Tipo de Cartão:</span>
                        <span style={valueStyle}>{tipoCartaoMap[pedido.cartaoTipo] || pedido.cartaoTipo}</span>
                    </div>
                    {pedido.cartaoObservacao && (
                        <div style={{ ...itemStyle, borderBottom: "none" }}>
                            <span style={labelStyle}>Observações:</span>
                            <span style={valueStyle}>{pedido.cartaoObservacao}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Se não houver forma de pagamento definida, não renderizar nada
    if (!formaPagamento) {
        return null;
    }

    return (
        <div>
            {/* Renderizar seção específica baseada na forma de pagamento */}
            {formaPagamento === "PIX" && renderPixDetails()}
            {formaPagamento === "TRANSFERENCIA" && renderDadosBancarios()}
            {formaPagamento === "BOLETO" && renderBoletoDetails()}
            {formaPagamento === "CARTAO" && renderCartaoDetails()}

            {/* Renderizar detalhes do parcelamento se houver */}
            {isPagamentoParcelado && renderParcelamentoDetails()}

            {/* Observações adicionais sobre pagamento */}
            {pedido.pagamentoObservacoes && (
                <div style={sectionStyle}>
                    <h3 style={titleStyle}>
                        <span>📝</span>
                        Observações sobre Pagamento
                    </h3>
                    <p style={{ fontSize: "14px", color: "#374151", lineHeight: "1.6", margin: 0 }}>
                        {pedido.pagamentoObservacoes}
                    </p>
                </div>
            )}
        </div>
    );
};

export default DetalhesPagamentoView;
